import { useRef, useState, type KeyboardEvent } from 'react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { CRM_ICON_NAMES } from '../icons'
import { CrmIcon } from './CrmIcon'

/** Icons to a row of the grid (`grid-cols-6`): up and down move a whole row. */
const COLUMNS = 6

/** The key of "Geen icoon" among the options; no icon is called this. */
const NONE = ''

interface IconOption {
  key: string
  icon: string | null
  name: string
}

/** Where a key moves in a grid of `count` options, or null for a key that does not. */
function step(key: string, index: number, count: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (index + 1) % count
    case 'ArrowLeft':
      return (index - 1 + count) % count
    case 'ArrowDown':
      return index + COLUMNS < count ? index + COLUMNS : index
    case 'ArrowUp':
      return index >= COLUMNS ? index - COLUMNS : index
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}

/**
 * Pick an icon from the fixed set, with a search field (design §4.8). Like the colour
 * picker, the grid is one stop in the tab order (roving tabindex): Tab enters on the
 * current icon (or the first match), the arrow keys move on, left and right wrap around,
 * Home and End jump to the ends. Moving is not choosing: a choice saves, so it takes
 * Enter or Space.
 */
export function IconPicker({
  value,
  onChange,
  allowNone = false,
  label = 'Icoon',
}: {
  value: string | null
  onChange: (icon: string | null) => void
  /** Labels may have no icon (a colour dot instead); categories always have one. */
  allowNone?: boolean
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // The option the arrow keys last moved to; null = the current icon (or the first
  // option when the current one is not on show).
  const [moved, setMoved] = useState<string | null>(null)
  const buttons = useRef(new Map<string, HTMLButtonElement>())
  const needle = query.trim().toLowerCase()
  const matches = CRM_ICON_NAMES.filter((name) => name.includes(needle))
  const options: IconOption[] = [
    ...(allowNone ? [{ key: NONE, icon: null, name: 'Geen icoon' }] : []),
    ...matches.map((name) => ({ key: name, icon: name, name })),
  ]
  const current = value ?? NONE
  const tabStop =
    moved ?? (options.some((option) => option.key === current) ? current : options[0]?.key)

  // However the popover closes (a choice, Escape, a click outside), the next opening
  // starts with an empty search on the current icon; a leftover filter would look like
  // missing icons.
  function changeOpen(next: boolean) {
    setOpen(next)
    if (!next) {
      setQuery('')
      setMoved(null)
    }
  }

  function choose(icon: string | null) {
    onChange(icon)
    changeOpen(false)
  }

  function move(event: KeyboardEvent, index: number) {
    const next = step(event.key, index, options.length)
    if (next === null) return
    event.preventDefault()
    const { key } = options[next]
    setMoved(key)
    buttons.current.get(key)?.focus()
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${value ?? 'geen'}`}
          className="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background hover:bg-muted"
        >
          {value ? (
            <CrmIcon name={value} className="size-4" />
          ) : (
            <span className="text-xs text-muted-foreground">–</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            // A new search starts on the current icon again, or on the first match.
            setMoved(null)
          }}
          placeholder="Zoek icoon…"
          aria-label="Zoek icoon"
          className="mb-2 h-8"
        />
        <div
          role="radiogroup"
          aria-label={label}
          className="grid max-h-56 grid-cols-6 gap-1 overflow-y-auto"
        >
          {options.map((option, index) => (
            <button
              key={option.icon ?? 'geen-icoon'}
              ref={(node) => {
                if (node) buttons.current.set(option.key, node)
                else buttons.current.delete(option.key)
              }}
              type="button"
              role="radio"
              aria-label={option.name}
              aria-checked={option.key === current}
              tabIndex={option.key === tabStop ? 0 : -1}
              onKeyDown={(event) => move(event, index)}
              onClick={() => choose(option.icon)}
              className={cn(
                'flex size-9 items-center justify-center rounded-md hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
                option.icon === null && 'text-xs text-muted-foreground',
                option.key === current && 'bg-muted ring-1 ring-ring',
              )}
            >
              {option.icon ? <CrmIcon name={option.icon} className="size-4" /> : '–'}
            </button>
          ))}
        </div>
        {matches.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">Geen iconen gevonden.</p>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
